import { useState, type FC } from "react";

import type { OutgoingIntakeReminderSchedule } from "@/api/types/outgoingIntake";
import { Field, FieldLabel } from "@/components/ui/field";
import { FilterChip } from "@/components/ui/filter-chip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import {
  BEFORE_EXPIRY_CHOICES,
  CUSTOM_PRESET_ID,
  DAY_CHOICES,
  REMINDER_PRESETS,
  STARTER_CUSTOM,
  describeReminders,
  reminderPresetId,
  toggleDay,
  withBeforeExpiry
} from "./reminders";

type Schedule = OutgoingIntakeReminderSchedule | null;

/**
 * The cadence the picker shows: a preset, or "custom" once the schedule
 * matches none (or the attorney chose Custom…, even before changing a day).
 */
export const useReminderCadence = (
  value: Schedule,
  onChange: (next: Schedule) => void
) => {
  const [editing, setEditing] = useState(false);
  const presetId = editing ? CUSTOM_PRESET_ID : reminderPresetId(value);
  const choose = (id: string) => {
    if (id === CUSTOM_PRESET_ID) {
      setEditing(true);
      if (!value) onChange(STARTER_CUSTOM);
      return;
    }
    setEditing(false);
    const preset = REMINDER_PRESETS.find((p) => p.id === id);
    if (preset) onChange(preset.schedule);
  };
  return { presetId, choose, custom: presetId === CUSTOM_PRESET_ID };
};

type SelectProps = {
  value: Schedule;
  presetId: string;
  onChoose: (id: string) => void;
  disabled?: boolean;
  label?: string;
  /** Extra-small text, for the IDE panes; off to match a sheet's other fields. */
  dense?: boolean;
  className?: string;
};

export const ReminderPresetSelect: FC<SelectProps> = ({
  value,
  presetId,
  onChoose,
  disabled,
  label = "Reminders",
  dense = true,
  className
}) => {
  const text = dense ? "text-xs" : undefined;
  const labelText = dense ? "text-xs" : "text-sm";
  return (
    <Field className={className}>
      <FieldLabel className={labelText}>{label}</FieldLabel>
      <Select value={presetId} disabled={disabled} onValueChange={onChoose}>
        <SelectTrigger
          size="sm"
          className={cn("w-full", text)}
          data-testid="reminder-preset"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {REMINDER_PRESETS.map((p) => (
            <SelectItem key={p.id} value={p.id} className={text}>
              {p.label}
            </SelectItem>
          ))}
          <SelectItem value={CUSTOM_PRESET_ID} className={text}>
            {presetId === CUSTOM_PRESET_ID
              ? describeReminders(value)
              : "Custom…"}
          </SelectItem>
        </SelectContent>
      </Select>
    </Field>
  );
};

type CustomProps = {
  value: Schedule;
  onChange: (next: Schedule) => void;
  disabled?: boolean;
  dense?: boolean;
  className?: string;
};

/** Pick the days after sending and how long before expiry to remind. */
export const CustomReminders: FC<CustomProps> = ({
  value,
  onChange,
  disabled,
  dense = true,
  className
}) => {
  const text = dense ? "text-xs" : undefined;
  const labelText = dense ? "text-xs" : "text-sm";
  const schedule = value ?? STARTER_CUSTOM;
  const days = Array.from(
    new Set([...DAY_CHOICES, ...schedule.days_after_send])
  ).sort((a, b) => a - b);
  return (
    <div
      className={cn("flex flex-col gap-3", className)}
      data-testid="custom-reminders"
    >
      <Field>
        <FieldLabel className={labelText}>Days After Sending</FieldLabel>
        <div className="flex flex-wrap gap-1.5">
          {days.map((d) => (
            <FilterChip
              key={d}
              active={schedule.days_after_send.includes(d)}
              disabled={disabled}
              onClick={() => onChange(toggleDay(schedule, d))}
              aria-label={`Day ${d}`}
            >
              Day {d}
            </FilterChip>
          ))}
        </div>
      </Field>
      <Field>
        <FieldLabel className={labelText}>Before the Link Expires</FieldLabel>
        <Select
          value={String(schedule.days_before_expiry ?? 0)}
          disabled={disabled}
          onValueChange={(v) =>
            onChange(withBeforeExpiry(schedule, Number(v) || null))
          }
        >
          <SelectTrigger
            size="sm"
            className={cn("w-full", text)}
            data-testid="reminder-before-expiry"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0" className={text}>
              No Reminder
            </SelectItem>
            {BEFORE_EXPIRY_CHOICES.map((d) => (
              <SelectItem key={d} value={String(d)} className={text}>
                {d} {d === 1 ? "day" : "days"} before
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
};
